import BaseComponent from '../base-component/base-component'
import EmblaCarousel, {
  EmblaCarouselType,
  EmblaPluginType,
} from 'embla-carousel'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import ClassNamesPlugin from 'embla-carousel-class-names'
import Carousel from './carousel'
import { debounce } from '../../utils/debounce'
import { EmblaCarouselOptions } from './embla-types'

class CarouselDots extends BaseComponent {
  embla: EmblaCarouselType

  constructor() {
    super()
  }

  connectedCallback(): void {
    if (this.clientWidth === 0) {
      return
    }

    this.setCarousel()
    this.update()

    if (this.carousel) {
      this.carousel.embla.on('reInit', this.handleCarouselReInit)
      this.carousel.embla.on('select', this.handleCarouselSelect)
    }

    this.embla.on('resize', this.handleResize)
    this.embla.on('settle', this.handleSettle)
  }

  disconnectedCallback(): void {
    if (this.clientWidth === 0) {
      return
    }

    if (this.carousel) {
      this.carousel.embla.off('reInit', this.handleCarouselReInit)
      this.carousel.embla.off('select', this.handleCarouselSelect)
    }

    this.embla.off('settle', this.handleSettle)
    this.embla.off('resize', this.handleResize)
    this.embla.destroy()
  }

  handleSettle = (): void => {
    this.update()
  }

  handleCarouselReInit = (): void => {
    this.embla.reInit(this.getOptions(), this.getPlugins())
  }

  handleResize = debounce((): void => {
    this.embla.reInit(this.getOptions(), this.getPlugins())

    this.update()
  }, 100)

  reInit(): void {
    this.embla.reInit(this.getOptions(), this.getPlugins())
  }

  handleCarouselSelect = (): void => {
    if (!this.carousel) {
      return
    }

    const dots = [...this.embla.slideNodes()]
    const selectedDotIndex = this.carousel.embla.selectedScrollSnap()

    dots.forEach(dot => {
      const isSelectedDot = +dot.dataset.dotIndex === selectedDotIndex

      dot.classList.toggle('is-primary', isSelectedDot)
    })

    this.embla.scrollTo(selectedDotIndex)
  }

  getOptions = (): EmblaCarouselOptions => ({
    inViewThreshold: +(this.dataset.inViewThreshhold || 0) || 0.5,
    containScroll: this.getAttribute('data-contain-scroll'),
    align: this.getAttribute('data-align'),
    speed: 80,
    skipSnaps: this.hasAttribute('data-skip-snaps'),
    draggable: this.hasAttribute('is-draggable'),
    axis: this.getAttribute('data-axis'),
  })

  getPlugins = (): EmblaPluginType[] => {
    const plugins: EmblaPluginType[] = []
    const classNamesPlugin = ClassNamesPlugin()

    plugins.push(classNamesPlugin)

    if (this.hasAttribute('data-with-wheel-gestures')) {
      const wheelGesturesPlugin = WheelGesturesPlugin({
        forceWheelAxis: this.getAttribute('data-axis'),
      })

      plugins.push(wheelGesturesPlugin)
    }

    return plugins
  }

  setCarousel(): void {
    const viewport = this.querySelector<HTMLElement>('[data-carousel-viewport]')

    if (!viewport) {
      return
    }

    this.embla = EmblaCarousel(viewport, this.getOptions(), this.getPlugins())
  }

  update(): void {
    const dots = [...this.embla.slideNodes()]
    const dotsIndexList = dots.map((_, index) => index)
    const visibleDotIndexList = this.embla.slidesInView()

    dots.forEach((dot, index) => {
      dot.classList.remove('is-prev-prev')

      const firstVisibleDotIndex = visibleDotIndexList[0]
      const lastVisibleDotIndex =
        visibleDotIndexList[visibleDotIndexList.length - 1]

      const firstDotIndex = dotsIndexList[0]
      const lastDotIndex = dotsIndexList[dotsIndexList.length - 1]

      const isLastDot = index === lastDotIndex
      const isLastVisibleDot = index === lastVisibleDotIndex

      const isFirstDot = index === firstDotIndex
      const isFirstVisibleDot = index === firstVisibleDotIndex

      if (
        (isFirstVisibleDot || isLastVisibleDot) &&
        !isLastDot &&
        !isFirstDot
      ) {
        dot.classList.add('is-prev-prev')
      }
    })
  }

  scrollNext = (): void => {
    if (!this.carousel) {
      return
    }

    this.carousel.embla.scrollNext()
  }

  scrollPrev = (): void => {
    if (!this.carousel) {
      return
    }

    this.carousel.embla.scrollPrev()
  }

  canScrollNext = (): boolean => {
    if (!this.carousel) {
      return false
    }

    return this.carousel.canScrollNext()
  }

  canScrollPrev = (): boolean => {
    if (!this.carousel) {
      return false
    }

    return this.carousel.canScrollPrev()
  }

  get carousel() {
    return this.closest<Carousel>('carousel-component')
  }
}

export default CarouselDots
